import { useCallback, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { styled } from "styled-components";

import type { OpenAPI } from "simplydo/interfaces";
import { CheckboxHeader } from "simplydo/react/components/CheckboxHeader";
import {
  Button,
  Divider,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  Icon,
} from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { UserChooser } from "components/lib/Choosers";
import { AssessorAudienceWarning } from "components/lib/Choosers/UserChooser/Content/ChallengeAudienceWarning";

import IdeaAssessmentReport from "../../IdeaAssessmentReport";
import type {
  IdeaPreviewChallenge,
  IdeaPreviewIdea,
  IdeaWithAssessmentDetails,
} from "../types";
import { SendAssessmentFeedbackModal } from "./SendAssessmentFeedbackModal";

const SubSegment = styled.div`
  display: flex;
  justify-content: space-between;
  background-color: #f7f7f7;
  border-radius: 5px;
  padding: 5px;
`;

const SubSegmentContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 5px;
`;

type ChooserUser = OpenAPI.Schemas["User"] & { isEmailInvitee?: boolean };

type AssessorManagementProps = {
  idea: IdeaWithAssessmentDetails;
  challenge?: IdeaPreviewChallenge;
};

export const AssessorManagement = ({
  idea,
  challenge,
}: AssessorManagementProps) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);
  const [assessmentFeedbackModalOpen, setAssessmentFeedbackModalOpen] =
    useState(false);

  const template = challenge?.ownerIdeaTemplate;
  const assessmentRounds = template?.assessments?.rounds || [];
  const [searchParams, setSearchParams] = useSearchParams();

  const addAssessors = useCallback(
    (users: ChooserUser[], assessmentRoundId: string) => {
      if (!challenge) {
        return;
      }

      const usersToAdd = users.filter((assessor) => !assessor.isEmailInvitee);
      if (usersToAdd.length === 0) {
        return;
      }
      api.challenges.assignAssessors(
        challenge._id,
        [idea._id],
        usersToAdd.map((u) => u._id),
        assessmentRoundId,
        ({ ideas }) => {
          const updatedIdea = ideas[0];
          if (updatedIdea) {
            util.onUpdateIdea(idea, { assessments: updatedIdea.assessments });
          }
          toast.success("Assessors updated");
        },
        (err) => toast.error(err.message),
      );
    },
    [idea, challenge],
  );

  const currentAssessmentRoundId = searchParams.get("assessmentRound");

  const { assessments } = template ?? {};
  if (!challenge) {
    return null;
  }

  const canEditAssessments =
    util.canManageChallenge(user!, challenge) ||
    util.hasPermission(user!, "challenge.editAssessments", idea.challenge);

  return (
    <div>
      {canEditAssessments ?
        <>
          <SubSegment>
            <SubSegmentContent>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                }}
              >
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {assessmentRounds.length > 1 ?
                    <Dropdown
                      pointing="top right"
                      icon={null}
                      trigger={
                        <Button
                          size="small"
                          content={
                            <p>
                              <Icon name="user plus" /> Assign assessors
                            </p>
                          }
                        />
                      }
                    >
                      <DropdownMenu>
                        {assessmentRounds.map(
                          (round: { id: string; name: string }) => (
                            <UserChooser
                              key={round.id}
                              trigger={
                                <DropdownItem>{round.name}</DropdownItem>
                              }
                              enabledFeatures={{ search: true, invite: true }}
                              onComplete={(users) => {
                                const chooserUsers = users as ChooserUser[];
                                const emailUsers = chooserUsers.filter(
                                  (u) => u.isEmailInvitee,
                                );
                                if (emailUsers.length) {
                                  api.invitations.createBulk(
                                    {
                                      invitees: emailUsers.map((u) => u._id),
                                      invitationType: "email",
                                      forType: "ideaAssessor",
                                      forId: idea.challenge,
                                      forIdeas: [idea._id],
                                      ideaDetails: {
                                        [idea._id]: {
                                          assessmentRounds: [round.id],
                                        },
                                      },
                                    },
                                    () => {
                                      toast.success(
                                        `Invitation${emailUsers.length > 1 ? "s" : ""} sent`,
                                      );
                                      api.invalidateQueries(
                                        api.invitations.getForType,
                                        ["ideaAssessor", idea.challenge],
                                      );
                                    },
                                    () => {},
                                  );
                                }
                                addAssessors(chooserUsers, round.id);
                              }}
                              forId={challenge?._id}
                              afterContent={() =>
                                (
                                  !challenge?.visibility?.organisations?.includes(
                                    user!.organisation,
                                  )
                                ) ?
                                  <AssessorAudienceWarning />
                                : null
                              }
                              clearOnComplete
                              forType="assessor"
                              subtitle={`Choose users to assign as assessors of the selected ${t("generic.ideas")}.`}
                              confirm="Assign these assessors"
                              searchFunction={(term, cb, fail) =>
                                api.search.challengeAssessors(
                                  idea.challenge,
                                  term,
                                  ({ users }) => cb(users),
                                  fail,
                                )
                              }
                            />
                          ),
                        )}
                      </DropdownMenu>
                    </Dropdown>
                  : <UserChooser
                      trigger={
                        <Button
                          size="small"
                          content={
                            <p>
                              <Icon name="user plus" /> Assign assessors
                            </p>
                          }
                        />
                      }
                      enabledFeatures={{ search: true, invite: true }}
                      onComplete={(users) => {
                        const emailUsers = users.filter(
                          (u) => u.isEmailInvitee,
                        );
                        if (emailUsers.length) {
                          api.invitations.createBulk(
                            {
                              invitees: emailUsers.map((u) => u._id),
                              invitationType: "email",
                              forType: "ideaAssessor",
                              forId: idea.challenge,
                              forIdeas: [idea._id],
                              ideaDetails: {
                                [idea._id]: {
                                  assessmentRounds: [
                                    assessmentRounds[0]?.id ?? "",
                                  ],
                                },
                              },
                            },
                            () => {
                              toast.success(
                                `Invitation${emailUsers.length > 1 ? "s" : ""} sent`,
                              );
                              api.invalidateQueries(
                                api.invitations.getForType,
                                ["ideaAssessor", idea.challenge],
                              );
                            },
                            () => {},
                          );
                        }
                        addAssessors(
                          users as ChooserUser[],
                          assessmentRounds[0]?.id ?? "",
                        );
                      }}
                      forId={challenge?._id}
                      afterContent={() =>
                        (
                          !challenge?.visibility?.organisations?.includes(
                            user!.organisation,
                          )
                        ) ?
                          <AssessorAudienceWarning />
                        : null
                      }
                      clearOnComplete
                      forType="assessor"
                      subtitle={`Choose users to assign as assessors of the selected ${t("generic.ideas")}.`}
                      confirm="Assign these assessors"
                      searchFunction={(term, cb, fail) =>
                        api.search.challengeAssessors(
                          idea.challenge,
                          term,
                          ({ users }) => cb(users),
                          fail,
                        )
                      }
                    />
                  }
                  <Button
                    size="small"
                    onClick={() => setAssessmentFeedbackModalOpen(true)}
                    content={
                      <p>
                        <Icon name="send" /> Send feedback
                      </p>
                    }
                  />
                </div>
              </div>
            </SubSegmentContent>
          </SubSegment>
          <Divider hidden />
        </>
      : null}
      <div>
        {(assessments?.rounds?.length ?? 0) > 0 ?
          <div style={{ marginBottom: 25 }}>
            <h5 style={{ color: "#333", marginBottom: 0 }}>
              Select assessment to review
            </h5>
            <p>
              Automated assessment feedback will be shown in-line with the{" "}
              {t("generic.idea")} content.
            </p>
            <Dropdown
              selection
              clearable
              fluid
              placeholder="Select assessment"
              value={currentAssessmentRoundId ?? undefined}
              selectOnNavigation={false}
              selectOnBlur={false}
              onChange={(e, { value }) => {
                setSearchParams((prev) => {
                  if (!value) {
                    prev.delete("assessmentRound");
                    return prev;
                  }
                  prev.set("assessmentRound", value as string);
                  return prev;
                });
              }}
              options={assessments?.rounds?.map(
                (round: { id: string; name: string }) => ({
                  key: round.id,
                  text: round.name,
                  value: round.id,
                }),
              )}
            />
            <Divider />
          </div>
        : null}
      </div>
      <IdeaAssessmentReport
        idea={idea as IdeaPreviewIdea}
        challenge={challenge}
        fixedHeight={null}
      />
      <SendAssessmentFeedbackModal
        idea={idea as IdeaPreviewIdea & IdeaWithAssessmentDetails}
        challenge={challenge}
        isOpen={assessmentFeedbackModalOpen}
        setOpen={setAssessmentFeedbackModalOpen}
      />
    </div>
  );
};
